import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { HOME_OS_MODULES } from "../data/homeOsModuleConfig";
import { sectionTheme } from "../theme/homeOsSectionThemes";

/* ════════════════════════════════════════════════════════════════════════
   HOME OS SECTION BENTO

   Equal-size bento tiles for each section of the Home Operating System.
   Each tile renders the section accent, soft icon bubble, a short human
   description, a live status line and a clear CTA pill.

   Inputs:
     • sections   — array of section descriptors (see below)
     • columns    — optional override (default responsive)

   Each section descriptor:
     {
       sectionId:    "today" | "financial" | ... ,    // theme lookup
       moduleKey:    "today" | "money" | ...,         // HOME_OS_MODULES lookup
       label:        "Financial",
       description:  "Money, goals and cashflow.",
       status:       "$X remaining",
       cta:          "Plan" | "Open" | ...,
       route:        "/home-os/money"  (optional; falls back to module.route)
     }
   ════════════════════════════════════════════════════════════════════════ */

export function HomeOSSectionBento({ sections, columns }) {
  const navigate = useNavigate();
  const gridCols =
    columns ||
    "grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";

  return (
    <section data-testid="section-os-bento">
      <div className="flex items-baseline justify-between mb-3 px-1">
        <h2 className="text-xl sm:text-2xl font-extrabold text-[var(--ara-ink)] tracking-tight">
          The household
        </h2>
        <p className="text-xs font-bold text-[var(--ara-ink-muted)]">
          Tap a section to dive in
        </p>
      </div>

      <div className={`grid ${gridCols} gap-3 sm:gap-4`}>
        {sections.map((s) => {
          const theme = sectionTheme(s.sectionId);
          const mod = HOME_OS_MODULES[s.moduleKey] || HOME_OS_MODULES.today;
          const Icon = theme.icon || mod.icon;
          const route = s.route || mod.route;

          return (
            <button
              key={s.sectionId + "-" + s.moduleKey}
              onClick={() => navigate(route)}
              className={
                "group text-left rounded-[22px] bg-white border p-4 sm:p-5 " +
                "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md " +
                "focus:outline-none focus:ring-2 focus:ring-offset-2 " +
                mod.color.tile + " " + mod.color.ring
              }
              style={{ minHeight: 168 }}
              data-testid={`os-section-tile-${s.sectionId}`}
            >
              {/* Icon bubble — section gradient */}
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-sm mb-3"
                style={{ background: theme.gradient }}
                aria-hidden
              >
                <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>

              <h3 className="text-base sm:text-lg font-extrabold text-[var(--ara-ink)] leading-tight tracking-tight">
                {s.label}
              </h3>
              <p className="text-[12px] font-medium text-[var(--ara-ink-muted)] mt-0.5 leading-snug line-clamp-2">
                {s.description}
              </p>

              {/* Status line — section accent */}
              <p
                className={`text-[13px] font-bold mt-3 leading-tight ${mod.color.textAccent}`}
                data-testid={`os-section-status-${s.sectionId}`}
              >
                {s.status}
              </p>

              {/* CTA row */}
              <div className="mt-3 pt-3 border-t border-[var(--ara-line)] flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold ${mod.color.textAccent}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${mod.color.dot}`} />
                  {theme.label}
                </span>
                <span className="inline-flex items-center gap-1 text-[12px] font-extrabold text-[var(--ara-ink)] group-hover:translate-x-0.5 transition-transform">
                  {s.cta || "Open"}
                  <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export default HomeOSSectionBento;
